import type { ReactElement } from "react"
import { useEffect, useState } from "react"
import SelectButtonAtm from "@onestore/hel/dist/components/atoms/SelectButtonAtm"
import TabsLinkMol from "@onestore/hel/dist/components/molecules/TabsLinkMol"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import FlexContainerOrg from "@onestore/hel/dist/components/organisms/FlexContainerOrg"
import HorizontalScrollItemsOrg from "@onestore/hel/dist/components/organisms/HorizontalScrollItemsOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import Header from "@gatsby-plugin-generic-page/components/Header"
import type { TabsSectionFlatData } from "@gatsby-plugin-generic-page/fragments/tabsSection"
import { GenericPageSectionsRenderer } from "@gatsby-plugin-generic-page/GenericPage"
import isDarkSectionBackground from "@gatsby-plugin-generic-page/helpers/isDarkSectionBackground"
import { useLocation } from "@reach/router"
import { get } from "lodash"
import { sendGaEvent } from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"
import SessionStorage from "~/lib/storage/SessionStorage"

export default function TabsSection({
  id,
  header,
  isCentered,
  hasButtonsAsLinks,
  hasSmallBottomSpace,
  tabs,
  sectionSettings,
}: TabsSectionFlatData): ReactElement<TabsSectionFlatData> {
  const tabsWithSection = tabs.filter((tab) => !isEmpty(tab.section))
  const { hash } = useLocation()
  const [activeButton, setActiveButton] = useState<string>("")
  const isDarkBackground = isDarkSectionBackground(sectionSettings)

  useEffect(() => {
    const storedTab = SessionStorage.get(`activeTab_${id}`)

    if (hash) {
      const locationHash = hash.replace("#", "")
      const tab = tabsWithSection.find((tab) => tab.id === locationHash)

      if (tab) {
        setActiveButton(tab.id)
      }
    } else if (storedTab && activeButton === "") {
      const tab = tabsWithSection.find(
        (tab) => tab.id === JSON.parse(storedTab)
      )

      if (tab) {
        setActiveButton(tab.id)
      }
    } else {
      setActiveButton(activeButton || tabsWithSection[0].id)

      SessionStorage.set(
        `activeTab_${id}`,
        JSON.stringify(activeButton || tabsWithSection[0].id)
      )
    }
  }, [hash])

  const handleTabClick = (tabId: string) => {
    const tab = tabs.find((tab) => tab.id === tabId)

    if (tab) {
      sendGaEvent(tab.gaEvent)
    }
    setActiveButton(tabId)
  }

  return (
    <>
      <BaseSection {...sectionSettings} hasOverflowHidden>
        <BaseContainer {...sectionSettings?.containerSettings}>
          {header ? (
            <PushOrg
              bottomSpace={hasSmallBottomSpace ? 4 : 8}
              leftSpace={{ small: 2, medium: 2, large: 0 }}
              rightSpace={{ small: 2, medium: 2, large: 0 }}
              hasNoReset
            >
              <AlignContentOrg
                horizontalAlign={{
                  small: get(header, "align.small.horizontalAlign", "left"),
                  medium: get(header, "align.medium.horizontalAlign", "center"),
                  large: get(header, "align.large.horizontalAlign", "center"),
                }}
              >
                <Header {...header} />
              </AlignContentOrg>
            </PushOrg>
          ) : null}
          <FlexContainerOrg
            justifyContent={isCentered ? "center" : "flex-start"}
          >
            <HorizontalScrollItemsOrg
              gutterSpace={2}
              bottomSpace={1}
              leftSpace={0.5}
              rightSpace={0.5}
              topSpace={0.5}
            >
              {hasButtonsAsLinks ? (
                <TabsLinkMol
                  tabs={tabsWithSection.map((tab) => ({
                    onClick: () => handleTabClick(tab.id),
                    text: tab.buttonText,
                    isActive: activeButton
                      ? activeButton === tab.id
                      : tabsWithSection[0].id === tab.id,
                  }))}
                  gutterSpace={3}
                />
              ) : (
                <>
                  {tabs.map((tab, index) => (
                    <SelectButtonAtm
                      activeColor={isDarkBackground ? "customLight" : undefined}
                      key={`${tab.buttonText}-${index}`}
                      title={tab.buttonText}
                      onClick={() => {
                        handleTabClick(tab.id)
                      }}
                      isActive={
                        activeButton
                          ? activeButton === tab.id
                          : tabsWithSection[0].id === tab.id
                      }
                    >
                      {tab.buttonText}
                    </SelectButtonAtm>
                  ))}
                </>
              )}
            </HorizontalScrollItemsOrg>
          </FlexContainerOrg>
        </BaseContainer>
      </BaseSection>

      <GenericPageSectionsRenderer
        pageId={activeButton}
        sections={
          tabs.find((tab) => tab.id === activeButton)?.section ||
          tabsWithSection[0].section
        }
      />
    </>
  )
}
